import { registerAuditLabels } from "@vixio/ui";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo research (ver modules/research/README.md). */

export type StudyStatus = "planning" | "ethics_review" | "approved" | "collecting" | "analysis" | "closed";
export type StudyKind = "validacao_instrumento" | "levantamento" | "intervencao" | "qualitativo" | "outro";
export type Purpose = "participacao" | "uso_dados" | "contato_futuro" | "gravacao" | "compartilhamento";
export type Decision = "pendencia" | "aprovado" | "nao_aprovado" | "retirado";

export const STUDY_STATUS: StatusMap = {
  planning: { label: "planejamento", tone: "neutral" },
  ethics_review: { label: "em análise no CEP", tone: "info" },
  approved: { label: "aprovado", tone: "ok" },
  collecting: { label: "em coleta", tone: "accent" },
  analysis: { label: "em análise", tone: "warn" },
  closed: { label: "encerrado", tone: "neutral" },
};

export const STEPS: Array<{ key: StudyStatus; label: string }> = [
  { key: "planning", label: "Planejamento" },
  { key: "ethics_review", label: "CEP" },
  { key: "approved", label: "Aprovado" },
  { key: "collecting", label: "Coleta" },
  { key: "analysis", label: "Análise" },
  { key: "closed", label: "Encerrado" },
];

export const KIND_LABEL: Record<StudyKind, string> = {
  validacao_instrumento: "Validação de instrumento",
  levantamento: "Levantamento",
  intervencao: "Intervenção",
  qualitativo: "Qualitativo",
  outro: "Outro",
};

export const ETHICS_STATUS: StatusMap = {
  submitted: { label: "submetido", tone: "info" },
  pending: { label: "com pendência", tone: "warn" },
  approved: { label: "aprovado", tone: "ok" },
  rejected: { label: "não aprovado", tone: "crit" },
  withdrawn: { label: "retirado", tone: "neutral" },
};

export const DECISION_LABEL: Record<Decision, string> = {
  pendencia: "Pendência",
  aprovado: "Aprovado",
  nao_aprovado: "Não aprovado",
  retirado: "Retirado",
};

export const PURPOSE_LABEL: Record<Purpose, string> = {
  participacao: "Participação",
  uso_dados: "Uso dos dados",
  contato_futuro: "Contato futuro",
  gravacao: "Gravação",
  compartilhamento: "Compartilhamento",
};

export const RISK_LABEL: Record<string, string> = { minimo: "Mínimo", baixo: "Baixo", moderado: "Moderado", alto: "Alto" };

export const CAAE_RE = /^[0-9]{8}\.[0-9]\.[0-9]{4}\.[0-9]{4}$/;

export interface StudyView {
  id: string;
  org_id: string;
  code: string;
  title: string;
  kind: StudyKind;
  instrument_ref: string | null;
  status: StudyStatus;
  requires_ethics: boolean;
  ethics_exemption_reason: string | null;
  institution: string | null;
  target_n: number | null;
  min_group_size: number;
  quasi_identifiers: string[];
  auto_enroll: boolean;
  principal_investigator: string | null;
  ethics_status: string | null;
  caae: string | null;
  committee_name: string | null;
  ethics_cleared: boolean;
  endorsement: "granted" | "revoked" | "none";
  participants_active: number;
  participants_withdrawn: number;
  created_at: string;
  updated_at: string;
}

export interface Study {
  id: string;
  objective: string;
  principal_investigator_person_id: string | null;
  participant_prefix: string;
}

export interface Protocol {
  id: string;
  study_id: string;
  version: string;
  title: string;
  summary: string;
  methodology: string;
  risk_level: string;
  involves_vulnerable: boolean;
  doc_id: string | null;
  submitted_at: string | null;
  created_at: string;
}

export interface EthicsRow {
  id: string;
  study_id: string;
  study_code: string;
  protocol_id: string;
  protocol_version: string;
  committee_name: string;
  platform: string;
  caae: string | null;
  status: "submitted" | "pending" | "approved" | "rejected" | "withdrawn";
  submitted_on: string;
  decided_on: string | null;
  opinions: Array<{ opinion_number: string; decision: Decision; issued_on: string; summary: string | null }> | null;
}

export interface Endorsement {
  id: string;
  entity: string;
  action: "granted" | "revoked";
  reference: string | null;
  note: string | null;
  created_at: string;
}

export interface ConsentTerm {
  id: string;
  purpose: Purpose;
  version: string;
  body: string;
  required: boolean;
  published_at: string;
}

export interface ParticipantView {
  id: string;
  study_id: string;
  code: string;
  attributes: Record<string, string | number | boolean>;
  status: "active" | "withdrawn";
  withdrawn_at: string | null;
  origin_type: string | null;
  accepted_participation: boolean;
  accepted_data_use: boolean;
  datapoints: number;
  created_at: string;
}

export interface ExportRow {
  id: string;
  purpose: string;
  min_group_size: number;
  quasi_identifiers: string[];
  rows_exported: number;
  rows_suppressed: number;
  created_at: string;
  created_by: string | null;
}

export type ExportResult =
  | { export_id: string; suppressed: true; min_group_size: number }
  | { export_id: string; suppressed: false; min_group_size: number; quasi_identifiers: string[]; n: number; rows_suppressed: number; rows: Array<Record<string, unknown>> };

declare module "@vixio/data" {
  interface RpcRegistry {
    "research.create_study": {
      args: {
        p_org: string;
        p_code: string;
        p_title: string;
        p_kind: StudyKind;
        p_objective: string;
        p_principal_investigator?: string | null;
        p_institution?: string | null;
        p_target_n?: number | null;
        p_instrument_ref?: string | null;
        p_quasi_identifiers?: string[];
        p_min_group_size?: number;
      };
      returns: string;
    };
    "research.update_study": {
      args: {
        p_study: string;
        p_title?: string | null;
        p_objective?: string | null;
        p_target_n?: number | null;
        p_quasi_identifiers?: string[] | null;
        p_min_group_size?: number | null;
        p_auto_enroll?: boolean | null;
        p_principal_investigator?: string | null;
      };
      returns: null;
    };
    "research.add_protocol_version": {
      args: { p_study: string; p_version: string; p_title: string; p_summary: string; p_methodology: string; p_risk_level?: string; p_involves_vulnerable?: boolean; p_doc_id?: string | null };
      returns: string;
    };
    "research.submit_to_ethics": { args: { p_study: string; p_protocol: string; p_committee_name: string; p_submitted_on?: string | null; p_caae?: string | null }; returns: string };
    "research.record_opinion": {
      args: { p_submission: string; p_opinion_number: string; p_decision: Decision; p_issued_on: string; p_summary?: string | null; p_caae?: string | null; p_doc_id?: string | null };
      returns: string;
    };
    "research.set_exemption": { args: { p_study: string; p_reason: string }; returns: null };
    "research.endorse": { args: { p_study: string; p_entity_org?: string | null; p_reference?: string | null; p_note?: string | null; p_doc_id?: string | null; p_entity?: string }; returns: string };
    "research.revoke_endorsement": { args: { p_study: string; p_note: string }; returns: string };
    "research.publish_tcle": { args: { p_study: string; p_purpose: Purpose; p_version: string; p_body: string; p_required?: boolean }; returns: string };
    "research.set_study_status": { args: { p_study: string; p_status: StudyStatus }; returns: null };
    "research.enroll_participant": { args: { p_study: string; p_attributes?: Record<string, unknown>; p_person?: string | null; p_origin_type?: string | null; p_origin_id?: string | null }; returns: { participant_id: string; code: string } };
    "research.record_acceptance": { args: { p_participant: string; p_purpose: Purpose; p_accepted: boolean; p_channel?: "online" | "presencial" | "gestao"; p_evidence?: Record<string, unknown> }; returns: number };
    "research.withdraw_participant": { args: { p_participant: string; p_reason?: string | null }; returns: null };
    "research.reveal_participant": { args: { p_participant: string }; returns: { code: string; person_id: string | null; full_name?: string; email?: string | null } };
    "research.export_anonymized": { args: { p_study: string; p_purpose: string }; returns: ExportResult };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "research.study_created": "Estudo criado",
  "research.study_updated": "Estudo atualizado",
  "research.study_status": "Situação do estudo alterada",
  "research.protocol_added": "Versão do protocolo registrada",
  "research.ethics_submitted": "Submetido ao CEP",
  "research.ethics_opinion": "Parecer do CEP registrado",
  "research.ethics_exempted": "Dispensa de CEP registrada",
  "research.study_endorsed": "Chancela InPES concedida",
  "research.endorsement_revoked": "Chancela retirada",
  "research.tcle_published": "TCLE publicado",
  "research.participant_enrolled": "Participante incluído",
  "research.participant_withdrawn": "Participante retirado",
  "research.participant_revealed": "Participante reidentificado",
  "research.acceptance_recorded": "Aceite registrado",
  "research.datapoint_added": "Dado registrado",
  "research.dataset_exported": "Exportação anonimizada",
});
